import { api } from '../../core/network/api';

export interface GoldPackage {
  id: string;
  name: string;
  priceCents: number;
  bars: number;
  tag: string | null;
  best: boolean;
}

export interface CheckoutResult {
  /** Provider hosted-checkout URL (redirect here for a real gateway). */
  url: string;
  /** Dev mock settles inline — when true, just refetch the balance. */
  completed: boolean;
  bars: number;
}

/** Custom-amount buy terms (buy any bar amount at the base rate). */
export interface CustomTerms {
  minBars: number;
  maxBars: number;
  centsPerBar: number;
}

/** A discounted limited-time bundle (dominion's special offers). */
export interface SpecialOffer {
  id: string;
  name: string;
  bars: number;
  priceCents: number;
  originalCents: number;
  savings: number;
  tag: 'FLASH' | 'HOT' | 'DEAL' | 'LIMITED' | string;
  color: string;
}

/** KYC purchase gate — spends ≥ threshold need an APPROVED verification when enabled. */
export interface KycTerms {
  enabled: boolean;
  thresholdCents: number;
  status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
}

export interface PackagesView {
  packages: GoldPackage[];
  offers: SpecialOffer[];
  /** Epoch ms when the current offer slate rotates (fresh deals every 8h). */
  offersEndAt: number;
  custom: CustomTerms;
  kyc: KycTerms;
  paymentMethods: PaymentMethod[];
}

/** GET /api/topup/packages — gold-bar packs + special offers + custom terms. */
export const fetchPackages = () => api.get<PackagesView>('/topup/packages');

/** POST /api/topup/checkout — a fixed package, a special offer, OR a custom bar amount. */
export type PaymentMethod = 'KLARNA' | 'YAPILY';

export const topupCheckout = (body: { packageId?: string; offerId?: string; bars?: number; paymentMethod?: PaymentMethod }) =>
  api.post<CheckoutResult>('/topup/checkout', body);

export interface PurchaseEntry {
  id: string;
  pack: string;
  bars: number;
  amountCents: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | string;
  createdAt: string;
}

/** GET /api/topup/history — this player's purchase ledger (newest first). */
export const fetchTopupHistory = () => api.get<PurchaseEntry[]>('/topup/history');

export interface InvoiceEntry {
  invoiceNumber: string;
  description: string;
  totalCents: number;
  currency: string;
  status: string;
  date: string;
  downloadUrl: string;
}

/** GET /api/topup/invoices — this player's invoices (newest first). */
export const fetchInvoices = () => api.get<InvoiceEntry[]>('/topup/invoices');


// ── Transaction ledger — every cash/gold movement (buys, sells, rewards…) ──
export interface LedgerEntry {
  id: string;
  entryType: 'CREDIT' | 'DEBIT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | string;
  currency: 'SOFT' | 'PREMIUM';
  /** Signed — credits positive, debits negative. */
  amount: number;
  balanceAfter: number;
  description: string;
  referenceId: string | null;
  createdAt: string;
}

/** GET /api/wallet/ledger — this player's full transaction history (newest first). */
export const fetchLedger = (params?: { limit?: number; currency?: 'SOFT' | 'PREMIUM' }) => {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.currency) q.set('currency', params.currency);
  const qs = q.toString();
  return api.get<LedgerEntry[]>(`/wallet/ledger${qs ? `?${qs}` : ''}`);
};
